import { ReactElement } from 'react';
import { createSlot } from '@radix-ui/react-slot';

import { useNoticeContext } from './context';
import { DefaultIcons } from './icons';

import css from './styles.module.css';

/**
 * Named so a bad child is reported against `Notice.Icon` rather than an
 * anonymous `Slot`.
 */
const IconSlot = createSlot('Notice.Icon');

export interface NoticeIconProps {
  /**
   * The icon element. Omit it for the variant's default.
   *
   * Typed as a single element because the icon slots onto its child rather
   * than wrapping it, so a string or a list of elements has nothing to slot
   * onto. A fragment satisfies the type but drops the class, so pass a real
   * element.
   */
  children?: ReactElement<{ className?: string }>;
}

/**
 * The notice's icon. Render `<Notice.Icon />` with no children to get the
 * variant's default icon, or pass a child to override it. A notice with no
 * `Notice.Icon` has no icon at all.
 *
 * Renders as the child itself rather than a wrapper, so the child must accept
 * a `className`.
 */
export function NoticeIcon({ children }: NoticeIconProps) {
  const { variant } = useNoticeContext();

  return (
    <IconSlot className={css.icon} aria-hidden>
      {children ?? DefaultIcons[variant]}
    </IconSlot>
  );
}
